/**
 * REPORT BRANDING — the `/next` half of an endpoint pair that has existed and worked for months.
 *
 * `GET`/`PUT /api/branding` (server/app.py:1136 and :1148) are live, session-authed and already
 * read on every build: `amb_report._run_job` loads the row and threads it into `meta.branding`
 * before the report is rendered, so the firm name, preparer, licence, contact, logo, palette and
 * the two bios land in the deliverable. The only thing missing was a form in this client.
 *
 * THE APEX SWAP IS WHAT MAKES THAT A GAP RATHER THAN AN INCONVENIENCE. Today the only way to change
 * branding is the form in the legacy app's `index.html` (`#sec-branding`, `loadBranding` at :3495).
 * The moment `valubrief.ai` serves this client and the legacy app moves to `old.valubrief.ai`, that
 * form stops being anywhere an agent would think to look — and every brief keeps rendering with
 * whatever was last saved through it, silently, with no way to correct it from the app they are in.
 * That is also where the wrong cover data gets fixed: it is a branding RECORD, not a code defect.
 *
 * Deliberately its own module in `lib/` rather than an addition to `cma/api.ts`: branding is not
 * part of the CMA flow, and a settings screen should not pull the wizard's module graph in with it.
 * Same reasoning `lib/useSession.ts` gives for itself.
 */
import { apiFetch } from "./auth";

/** Where the logo goes on the report. Mirrors `app._LOGO_PLACEMENTS` exactly. */
export type LogoPlacement = "banner" | "strip" | "off";

export interface Branding {
  firm_name: string | null;
  preparer_name: string | null;
  license_no: string | null;
  contact: string | null;
  logo_data_uri: string | null;
  logo_placement: LogoPlacement | null;
  show_valubrief_logo: boolean;
  theme_preset: string | null;
  agent_bio: string | null;
  agent_bio_verbatim: boolean;
  company_bio: string | null;
  company_bio_verbatim: boolean;
}

export interface ThemePreset {
  key: string;
  name: string;
}

export interface BrandingPayload {
  branding: Partial<Branding>;
  /** The server owns this list (`engine.brand_assets.PRESETS`); it is never hardcoded here. */
  presets: ThemePreset[];
  /** Likewise `app._LOGO_PLACEMENTS`. */
  placements: LogoPlacement[];
}

/**
 * The server's own cap, mirrored so the browser can refuse a too-large file before spending a
 * round trip on it. `app.MAX_LOGO_CHARS` is the real gate and stays the real gate — this is a
 * courtesy, not a substitute, which is why the PUT's 400 is still surfaced verbatim.
 */
export const MAX_LOGO_BYTES = 2_000_000;

export function getBranding(): Promise<BrandingPayload> {
  return apiFetch<BrandingPayload>("/api/branding");
}

export function putBranding(body: Partial<Branding>): Promise<{ branding: Branding }> {
  return apiFetch<{ branding: Branding }>("/api/branding", { method: "PUT", body });
}

/**
 * The empty shape the server returns for an agent who has never saved branding. Kept here so the
 * form renders identically whether the row exists or not, rather than special-casing null.
 */
export const EMPTY_BRANDING: Branding = {
  firm_name: null,
  preparer_name: null,
  license_no: null,
  contact: null,
  logo_data_uri: null,
  logo_placement: null,
  show_valubrief_logo: true,
  theme_preset: null,
  agent_bio: null,
  agent_bio_verbatim: false,
  company_bio: null,
  company_bio_verbatim: false,
};
